import { Edit2, Trash2, Calendar, CheckCircle2 } from 'lucide-react';
import { Card } from '@components/ui';
import { MATERIAL_CATEGORIES, UNIT_TYPES, calculateEstimatedCost } from '@mocks/malzemeler';
import clsx from 'clsx';

export default function MaterialCard({ material, onEdit, onDelete, formatCurrency }) {
    const category = MATERIAL_CATEGORIES[material.category];
    const estimatedCost = calculateEstimatedCost(material);
    const hasActual = material.actualQuantity != null && material.actualQuantity > 0;
    const progress = hasActual ? Math.min(100, (material.actualQuantity / material.estimatedQuantity) * 100) : 0;
    const isCompleted = hasActual && material.actualQuantity >= material.estimatedQuantity;

    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });

    return (
        <Card padding="none" className="overflow-hidden hover:shadow-md transition-shadow group">
            {/* Kategori renk bandı */}
            <div className={clsx("h-1.5", isCompleted ? 'bg-success' : hasActual ? 'bg-warning' : 'bg-primary')} />

            <div className="p-4">
                {/* Başlık ve Aksiyonlar */}
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-text-primary truncate">{material.name}</h3>
                            {isCompleted && <CheckCircle2 size={14} className="text-success shrink-0" />}
                        </div>
                        <span className="text-xs text-text-tertiary">{category?.label}</span>
                    </div>
                    <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={onEdit} className="p-2 rounded-lg hover:bg-bg-tertiary transition-colors" title="Düzenle">
                            <Edit2 size={14} className="text-text-secondary" />
                        </button>
                        <button onClick={onDelete} className="p-2 rounded-lg hover:bg-error/10 transition-colors" title="Sil">
                            <Trash2 size={14} className="text-error" />
                        </button>
                    </div>
                </div>

                {/* Detaylar */}
                <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between items-center">
                        <span className="text-text-tertiary">Birim Fiyat</span>
                        <span className="text-text-primary font-medium">
                            {formatCurrency(material.unitPrice)} / {UNIT_TYPES[material.unitType]}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-text-tertiary">Tahmini Miktar</span>
                        <span className="text-text-primary font-medium">
                            {material.estimatedQuantity.toLocaleString('tr-TR')} {UNIT_TYPES[material.unitType]}
                        </span>
                    </div>
                    {hasActual && (
                        <div className="flex justify-between items-center">
                            <span className="text-text-tertiary">Gerçek Miktar</span>
                            <span className={clsx("font-medium", isCompleted ? 'text-success' : 'text-warning')}>
                                {material.actualQuantity.toLocaleString('tr-TR')} {UNIT_TYPES[material.unitType]}
                            </span>
                        </div>
                    )}
                </div>

                {/* İlerleme Barı */}
                {hasActual && (
                    <div className="mt-3">
                        <div className="flex justify-between text-[11px] mb-1">
                            <span className="text-text-muted">İlerleme</span>
                            <span className={clsx("font-semibold", isCompleted ? 'text-success' : 'text-warning')}>
                                %{progress.toFixed(0)}
                            </span>
                        </div>
                        <div className="h-1.5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full transition-all duration-500",
                                isCompleted ? 'bg-success' : 'bg-warning'
                            )} style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                )}

                {/* Maliyet */}
                <div className="mt-3 pt-3 border-t border-border-light">
                    <div className="flex justify-between items-center">
                        <span className="text-text-secondary font-medium text-sm">Tahmini Maliyet</span>
                        <span className="text-lg font-bold text-primary">{formatCurrency(estimatedCost)}</span>
                    </div>
                </div>

                {/* Not ve tarih */}
                {material.notes && (
                    <p className="mt-2 text-xs text-text-muted line-clamp-1" title={material.notes}>{material.notes}</p>
                )}
                <div className="flex items-center gap-1 mt-1.5 text-xs text-text-muted">
                    <Calendar size={10} />
                    <span>Güncelleme: {formatDate(material.lastUpdated)}</span>
                </div>
            </div>
        </Card>
    );
}
